import { useEffect, useState } from "react";
import { Loader2, Search, Phone, MessageCircle, Trash2, Eye, Handshake } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ActivityTimeline } from "@/components/admin/ActivityTimeline";
import { toast } from "sonner";

const STATUSES = ["New","Sent To Buyer","Buyer Interested","Seller Contacted","Seller Interested","Negotiation","Site Visit","Deal In Process","Sold","Deal Closed","Not Interested","Lost"];
const COLOR = (s) => ({ New:"bg-orange-100 text-orange-700", Sold:"bg-emerald-100 text-emerald-700", "Deal Closed":"bg-emerald-100 text-emerald-700", Lost:"bg-slate-100 text-slate-500", "Not Interested":"bg-slate-100 text-slate-500" }[s] || "bg-blue-100 text-blue-700");
const waLink = (n) => { if (!n) return null; const d = n.replace(/\D/g, ""); return `https://wa.me/${d.length === 10 ? "91" + d : d}`; };
const telLink = (n) => (n ? `tel:${n.replace(/[^\d+]/g, "")}` : null);
const inr = (n) => `₹${new Intl.NumberFormat("en-IN").format(n || 0)}`;

export default function AdminCrmLeads() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [dealFor, setDealFor] = useState(null);

  const load = () => {
    setLoading(true);
    const params = {};
    if (status !== "all") params.status = status;
    if (search) params.search = search;
    api.get("/admin/leads", { params }).then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [status, search]);

  const setLeadStatus = async (id, s) => { try { await api.patch(`/admin/leads/${id}/status`, { status: s }); load(); } catch { toast.error("Failed"); } };
  const remove = async (id) => { if (!window.confirm("Delete this lead?")) return; try { await api.delete(`/admin/leads/${id}`); load(); } catch { toast.error("Failed"); } };
  const open = async (id) => { try { const { data } = await api.get(`/admin/leads/${id}`); setDetail(data); } catch { toast.error("Failed"); } };

  const waSeller = (l) => {
    const url = waLink(l.seller_whatsapp);
    if (!url) return null;
    const msg = `*Mashiniya Lead ${l.lead_code}*\nA buyer is interested in ${l.machine_name} (${l.machine_code}).\nBuyer: ${l.buyer_name}\nPlease confirm availability and your best price.`;
    return `${url}?text=${encodeURIComponent(msg)}`;
  };
  const waBuyer = (l) => {
    const url = waLink(l.buyer_whatsapp);
    if (!url) return null;
    const msg = `Hello ${l.buyer_name}, regarding your enquiry for ${l.machine_name} on Mashiniya — we have found a suitable option. Can we share the details?`;
    return `${url}?text=${encodeURIComponent(msg)}`;
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-extrabold text-slate-900">Leads</h1>
        <div className="w-48">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="lead-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="mt-4 relative max-w-md">
        <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
        <Input data-testid="lead-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search lead ID or machine…" className="pl-9" />
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
        <div className="mt-5 space-y-3">
          {items.length === 0 && <p className="py-12 text-center text-slate-500">No leads yet. Create leads from Requirements → Find Matches or Machines → Find Buyers.</p>}
          {items.map((l) => (
            <div key={l.id} data-testid={`lead-row-${l.lead_code}`} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[11px] text-slate-400">{l.lead_code}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${COLOR(l.status)}`}>{l.status}</span>
                    {l.match_score > 0 && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">{l.match_score}% match</span>}
                  </div>
                  <p className="mt-0.5 font-semibold text-slate-900">{l.machine_name} <span className="font-mono text-xs text-slate-400">{l.machine_code}</span></p>
                  <p className="text-sm text-slate-500">Buyer: {l.buyer_name} ({l.buyer_code}) • 📞 {l.buyer_mobile}</p>
                  <p className="text-sm text-slate-500">Seller: {l.seller_name} • {inr(l.machine_price)}</p>
                  {l.next_followup && <p className="text-xs text-orange-600">Next follow-up: {String(l.next_followup).slice(0, 10)}</p>}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="w-44">
                    <Select value={l.status} onValueChange={(v) => setLeadStatus(l.id, v)}>
                      <SelectTrigger data-testid={`lead-status-${l.lead_code}`} className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex flex-wrap justify-end gap-1.5">
                    {waBuyer(l) && <a href={waBuyer(l)} target="_blank" rel="noreferrer" title="WhatsApp Buyer" className="rounded-lg bg-whatsapp/90 p-2 text-white"><MessageCircle size={15} /></a>}
                    {telLink(l.buyer_mobile) && <a href={telLink(l.buyer_mobile)} title="Call Buyer" className="rounded-lg border border-slate-200 p-2 text-slate-600"><Phone size={15} /></a>}
                    {waSeller(l) && <a href={waSeller(l)} target="_blank" rel="noreferrer" title="WhatsApp Seller" className="rounded-lg bg-whatsapp p-2 text-white"><MessageCircle size={15} /></a>}
                    <button data-testid={`lead-deal-${l.lead_code}`} onClick={() => setDealFor(l)} title="Create Deal" className="rounded-lg border border-brand/30 bg-brand/5 p-2 text-brand"><Handshake size={15} /></button>
                    <button data-testid={`lead-view-${l.lead_code}`} onClick={() => open(l.id)} className="rounded-lg border border-slate-200 p-2 text-slate-600"><Eye size={15} /></button>
                    <button onClick={() => remove(l.id)} className="rounded-lg border border-red-200 p-2 text-red-500"><Trash2 size={15} /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {detail && <LeadDetail lead={detail} onClose={() => setDetail(null)} onRefresh={() => open(detail.id)} onCreateDeal={() => { setDealFor(detail); }} />}
      {dealFor && <DealModal lead={dealFor} onClose={() => setDealFor(null)} onSaved={() => { setDealFor(null); load(); }} />}
    </div>
  );
}

function LeadDetail({ lead, onClose, onRefresh, onCreateDeal }) {
  const [f, setF] = useState({ type: "Call", notes: "", next_followup_date: "", next_action: "" });
  const [saving, setSaving] = useState(false);
  const add = async () => {
    setSaving(true);
    try { await api.post(`/admin/leads/${lead.id}/followups`, { lead_id: lead.id, ...f }); toast.success("Follow-up added"); setF({ type: "Call", notes: "", next_followup_date: "", next_action: "" }); onRefresh(); }
    catch { toast.error("Failed"); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="lead-detail" className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader><DialogTitle>{lead.lead_code} — {lead.machine_name}</DialogTitle></DialogHeader>
        <div className="rounded-lg bg-slate-50 p-3 text-sm">
          <p><span className="font-semibold">Buyer:</span> {lead.buyer_name} • {lead.buyer_mobile}</p>
          <p><span className="font-semibold">Seller:</span> {lead.seller_name}</p>
          <p><span className="font-semibold">Status:</span> {lead.status}</p>
        </div>
        <button data-testid="lead-detail-deal" onClick={onCreateDeal} className="rounded-full bg-brand px-4 py-2 text-sm font-bold text-white">Create Deal</button>
        <h3 className="mt-2 font-display font-bold text-slate-900">Add Follow-up</h3>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Type</Label>
            <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{["Call","WhatsApp","Email","Site Visit","Other"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Next follow-up date</Label><Input data-testid="followup-date" type="date" value={f.next_followup_date} onChange={(e) => setF({ ...f, next_followup_date: e.target.value })} /></div>
        </div>
        <div><Label>Notes</Label><Textarea data-testid="followup-notes" rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
        <button data-testid="followup-save" onClick={add} disabled={saving} className="rounded-full bg-slate-900 px-4 py-2 text-sm font-bold text-white disabled:opacity-60">{saving ? "Saving…" : "Add Follow-up"}</button>

        <h3 className="mt-2 font-display font-bold text-slate-900">History ({lead.followups?.length || 0})</h3>
        <div className="space-y-2">
          {(lead.followups || []).map((fu) => (
            <div key={fu.id} className="rounded-lg border border-slate-100 p-2 text-sm">
              <p className="font-semibold text-slate-800">{fu.type} <span className="text-xs font-normal text-slate-400">{new Date(fu.created_at).toLocaleString("en-IN")}</span></p>
              {fu.notes && <p className="text-slate-600">{fu.notes}</p>}
              {fu.next_followup_date && <p className="text-xs text-orange-600">Next: {fu.next_followup_date}</p>}
            </div>
          ))}
          {(lead.followups || []).length === 0 && <p className="text-sm text-slate-400">No follow-ups yet.</p>}
        </div>

        <h3 className="mt-3 font-display font-bold text-slate-900">Activity</h3>
        <ActivityTimeline entityType="lead" entityId={lead.id} refreshKey={lead.followups?.length || 0} />
      </DialogContent>
    </Dialog>
  );
}

function DealModal({ lead, onClose, onSaved }) {
  const [f, setF] = useState({ final_sale_price: lead.machine_price || "", expected_closing_date: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.post("/admin/deals", { lead_id: lead.id, buyer_id: lead.buyer_id, machine_id: lead.machine_id,
        final_sale_price: Number(f.final_sale_price) || 0, expected_closing_date: f.expected_closing_date, notes: f.notes });
      toast.success("Deal created"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="deal-create-modal" className="max-w-md">
        <DialogHeader><DialogTitle>Create Deal — {lead.machine_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Final sale price (₹)</Label><Input data-testid="deal-price" type="number" value={f.final_sale_price} onChange={(e) => setF({ ...f, final_sale_price: e.target.value })} /></div>
          <div><Label>Expected closing date</Label><Input type="date" value={f.expected_closing_date} onChange={(e) => setF({ ...f, expected_closing_date: e.target.value })} /></div>
          <div><Label>Notes</Label><Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
          <button data-testid="deal-save" onClick={save} disabled={saving} className="w-full rounded-full bg-brand py-2.5 font-bold text-white disabled:opacity-60">{saving ? "Saving…" : "Create Deal"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
